import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { DurationSelector } from '@/core/DurationSelector.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import type { DurationValuesByUnit } from '@/core/DurationSelector.atom';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { DurationTimeUnit } from '@/main/app.constants';

export interface DurationTimeUnitValue {
  unit: DurationTimeUnit;
  value: number;
}

export interface DurationFormComponentProps extends ValidatingFormComponent<DurationTimeUnitValue> {
  component: 'DurationFormComponent';
  durationValuesByUnit?: DurationValuesByUnit;
  isCreatable?: boolean;
}

const defaultDurationValuesByUnit: DurationValuesByUnit = {};

export const DurationFormComponent = (props: DurationFormComponentProps) => {
  const {
    name,
    testId = 'duration',
    validation,
    extendValidation,
    customErrorText,
    required = false,
    durationValuesByUnit = defaultDurationValuesByUnit,
    isCreatable,
    warningText,
    label,
  } = props;

  const formState = useForm().getState();
  const showError = _.has(formState.errors, name) && formState.submitFailed && formState.hasValidationErrors;

  const defaultValidation = (value: DurationTimeUnitValue | undefined) => required && (!value?.value || !value?.unit);
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  return (
    <FormFieldWrapper
      label={label}
      wrapperXstyle={[styles.wrapper, props.wrapperXstyle]}
      fieldXstyle={[props.xstyle, props.fieldXstyle]}
      labelXstyle={props.labelXstyle}
      testId={testId}
      showError={showError}
      warningText={warningText}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const { onChange, value } = getFormFieldProps(formState, input, meta, props);

          return (
            <DurationSelector
              unit={value.unit}
              value={value.value}
              onChange={(value, unit) => onChange({ value, unit })}
              onInvalidInput={_.noop}
              durationValuesByUnit={durationValuesByUnit}
              isCreatable={isCreatable}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};

const styles = stylex.create({
  wrapper: { flex: '1 1 auto', overflow: 'hidden', marginBottom: 20 },
});
